/**
 * רישום ה־service worker והודעת ״יש גרסה חדשה״ (שלב 5).
 *
 * גרסה חדשה מותקנת ברקע ומחכה. ההודעה מופיעה בראש המסך; בלחיצה על ״טעינה
 * מחדש״ — טיוטות שבהקלדה נשמרות במכשיר, הגרסה החדשה נכנסת, והדף נטען מחדש.
 * התור והטיוטות ב־IndexedDB אינם נמחקים בעדכון (RQ-H11).
 *
 * בדיקת עדכון: בפתיחה, כשהאפליקציה חוזרת לקדמת המסך, וכל 30 דקות.
 */

import { useEffect, useState, type ReactElement } from 'react';
import { flushDrafts } from './sync/drafts.ts';

/** טעינה מחדש רק אחרי לחיצה — לא כשה־service worker הראשון נכנס לשליטה. */
let reloadRequested = false;

export function UpdateBanner(): ReactElement | null {
  const [waiting, setWaiting] = useState<ServiceWorker | null>(null);

  useEffect(() => {
    if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
    let registration: ServiceWorkerRegistration | null = null;

    // ״גרסה חדשה״ = גרסה שמחכה בזמן שגרסה אחרת פעילה. בהתקנה ראשונה אין גרסה
    // מחכה (היא נכנסת ישר) — ולכן אין הודעה. לא נשענים על סדר האירועים, שבו
    // WebKit ו־Chromium שונים.
    const check = (reg: ServiceWorkerRegistration) => {
      if (reg.waiting !== null && reg.active !== null) setWaiting(reg.waiting);
    };

    void navigator.serviceWorker
      .register('/sw.js')
      .then((reg) => {
        registration = reg;
        check(reg);
        reg.addEventListener('updatefound', () => {
          reg.installing?.addEventListener('statechange', () => check(reg));
        });
      })
      .catch(() => undefined); // בלי service worker האפליקציה עובדת — רק בלי מעטפת במכשיר

    const update = () => void registration?.update().catch(() => undefined);
    const onVisible = () => {
      if (document.visibilityState === 'visible') update();
    };
    document.addEventListener('visibilitychange', onVisible);
    const interval = setInterval(update, 30 * 60_000);

    const onController = () => {
      if (!reloadRequested) return;
      reloadRequested = false;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', onController);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(interval);
      navigator.serviceWorker.removeEventListener('controllerchange', onController);
    };
  }, []);

  if (waiting === null) return null;
  return (
    <div className="update-banner" role="status" data-testid="update-banner">
      <span>
        <strong>יש גרסה חדשה של האפליקציה.</strong> טיוטות ושינויים שעוד לא נשלחו שמורים במכשיר ולא
        יימחקו.
      </span>
      <button
        type="button"
        className="btn btn-primary"
        data-testid="update-reload"
        onClick={() => {
          reloadRequested = true;
          void flushDrafts().then(() => waiting.postMessage({ type: 'SKIP_WAITING' }));
        }}
      >
        טעינה מחדש
      </button>
    </div>
  );
}
